import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ThumbsUpIcon from "../../assets/feedback/thumbs-up.svg";
import ThumbsDownIcon from "../../assets/feedback/thumbs-down.svg";
import CheckmarkIcon from "../../assets/feedback/checkmark.svg";
import { allowOnlyAlphabetsAndNumbersAndSpaces } from "../../utils/validation";

const STEPS = {
  RATE: "rate",
  FEEDBACK: "feedback",
  THANKYOU: "thankyou",
};

const FEEDBACK_VALUES = {
  INACCURATE: "Results felt inaccurate",
  CONFUSING: "Confusing questions",
  HARD_TO_READ: "Report was hard to read",
  TECHNICAL: "Technical problem",
  SOMETHING_ELSE: "Something else",
};

const FEEDBACK_OPTIONS = [
  { value: FEEDBACK_VALUES.INACCURATE, key: "option_inaccurate" },
  { value: FEEDBACK_VALUES.CONFUSING, key: "option_confusing" },
  { value: FEEDBACK_VALUES.HARD_TO_READ, key: "option_hard_to_read" },
  { value: FEEDBACK_VALUES.TECHNICAL, key: "option_technical" },
  { value: FEEDBACK_VALUES.SOMETHING_ELSE, key: "option_something_else" },
];

function RateStep({ onYes, onNo }) {
  const { t } = useTranslation();

  return (
    <div>
      <h3 className="font-extrabold tracking-tight text-[clamp(1.2rem,3vw,1.45rem)] mt-1">
        {t("component.feedback_popup.rate_question")}
      </h3>
      <p className="text-[#6E635B] text-[0.95rem] mt-2.5">
        {t("component.feedback_popup.rate_subtitle")}
      </p>
      <div className="flex gap-3.5 justify-center mt-6">
        <button
          type="button"
          onClick={onYes}
          aria-label="Yes"
          style={{ border: "2px solid rgba(43,36,32,0.12)" }}
          className="flex flex-col items-center gap-2 flex-1 py-5 px-3 rounded-[18px] bg-white text-[#1F7A5A] cursor-pointer font-bold text-[0.92rem]"
        >
          <img
            src={ThumbsUpIcon}
            alt="Thumbs up"
            className="w-[30px] h-[30px]"
          />
          <span>{t("component.feedback_popup.yes")}</span>
        </button>
        <button
          type="button"
          onClick={onNo}
          aria-label="No"
          style={{ border: "2px solid rgba(43,36,32,0.12)" }}
          className="flex flex-col items-center gap-2 flex-1 py-5 px-3 rounded-[18px] bg-white text-[#C24326] cursor-pointer font-bold text-[0.92rem]"
        >
          <img
            src={ThumbsDownIcon}
            alt="Thumbs down"
            className="w-[30px] h-[30px]"
          />
          <span>{t("component.feedback_popup.no")}</span>
        </button>
      </div>
    </div>
  );
}

function FeedbackStep({ onSubmit }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [customReason, setCustomReason] = useState("");
  const [error, setError] = useState("");

  const isSomethingElse = selected.includes(FEEDBACK_VALUES.SOMETHING_ELSE);

  const handleCheckboxChange = (value) => {
    setSelected((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value];
      if (updated.length > 0) setError("");
      return updated;
    });
  };

  const handleSubmit = () => {
    if (selected.length === 0) {
      setError(t("report.common.feedback_required"));
      return;
    }
    if (isSomethingElse && !customReason.trim()) {
      setError(t("report.common.feedback_required"));
      return;
    }
    setError("");
    const reasons = [...selected];
    if (isSomethingElse && customReason.trim()) {
      reasons.push(customReason.trim());
    }
    onSubmit({ feedback: reasons.join(", ") });
  };

  const handleChange = (e) => {
    allowOnlyAlphabetsAndNumbersAndSpaces(e);
    setCustomReason(e.target.value);
    if (e.target.value.trim()) setError("");
  };

  return (
    <div className="text-left">
      <h3 className="font-extrabold tracking-tight text-[clamp(1.2rem,3vw,1.45rem)] mt-1">
        {t("component.feedback_popup.sorry_title")}
      </h3>
      <p className="text-[#6E635B] text-[0.95rem] mt-2.5">
        {t("component.feedback_popup.sorry_subtitle")}
      </p>
      <div className="flex flex-wrap gap-2 mt-[18px]">
        {FEEDBACK_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`flex items-center gap-[7px] py-2 px-[13px] border rounded-full text-[0.86rem] font-semibold cursor-pointer ${
              selected.includes(option.value)
                ? "border-[#C24326] bg-[#C24326]/10 text-[#C24326]"
                : "border-[#2B2420]/[0.14] bg-[#FFF7F2] text-[#4a423c]"
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => handleCheckboxChange(option.value)}
              className="accent-[#C24326] w-[15px] h-[15px] m-0"
            />
            {t(`component.feedback_popup.${option.key}`)}
          </label>
        ))}
      </div>
      {isSomethingElse && (
        <textarea
          value={customReason}
          onChange={handleChange}
          rows={2}
          maxLength={500}
          placeholder={t("component.feedback_popup.textarea_placeholder")}
          className="w-full mt-4 py-[13px] px-[15px] border border-[#2B2420]/[0.14] rounded-xl text-[0.95rem] resize-y bg-white text-[#2B2420]"
        />
      )}
      {error && <p className="mt-2 text-[0.85rem] text-[#C24326]">{error}</p>}
      <button
        type="button"
        onClick={handleSubmit}
        className="w-full mt-3.5 py-[15px] border-none rounded-[14px] bg-[#FF8C6B] text-white font-bold text-base cursor-pointer"
      >
        {t("component.feedback_popup.send_feedback")}
      </button>
    </div>
  );
}

function ThankYouStep({ onClose }) {
  const { t } = useTranslation();

  return (
    <div>
      <div className="w-16 h-16 mt-1.5 mx-auto rounded-full bg-[#E9F8F2] flex items-center justify-center">
        <img src={CheckmarkIcon} alt="Checkmark" className="w-8 h-8" />
      </div>
      <h3 className="font-extrabold tracking-tight text-[1.3rem] mt-4">
        {t("component.feedback_popup.thankyou_title")}
      </h3>
      <p className="text-[#6E635B] text-[0.95rem] mt-2.5">
        {t("component.feedback_popup.thankyou_subtitle")}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="w-full mt-5 py-[15px] border-none rounded-[14px] bg-[#2B2420] text-[#FFF7F2] font-bold text-base cursor-pointer"
      >
        {t("component.feedback_popup.back_to_results")}
      </button>
    </div>
  );
}

function FeedbackPopup({ isOpen, setIsOpen, onProceed, onClose, onSubmit }) {
  const [step, setStep] = useState(STEPS.RATE);
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleClose = () => {
    setIsOpen(false);
    setStep(STEPS.RATE);
    if (onClose) onClose();
  };

  const handleYes = () => {
    setIsOpen(false);
    setStep(STEPS.RATE);
    onProceed();
  };

  const handleFeedbackSubmit = async (payload) => {
    try {
      if (onSubmit) await onSubmit(payload);
      setStep(STEPS.THANKYOU);
    } catch (err) {
      toast.error(t("report.common.feedback_error_msg"));
    }
  };

  return (
    <div className="fixed top-[60px] md:top-[80px] inset-x-0 bottom-0 z-[200] overflow-y-auto animate-[fadeIn_0.3s_ease-out]">
      <div
        onClick={handleClose}
        className="fixed top-[60px] md:top-[80px] inset-x-0 bottom-0 z-[200] bg-[#2B2420]/45"
      />
      <div className="flex min-h-full items-center justify-center p-5">
        <div className="relative z-[201] w-full max-w-[420px] bg-white rounded-3xl p-6 sm:p-8 shadow-[0_30px_70px_-20px_rgba(43,36,32,0.55)] text-center animate-[slideUp_0.4s_ease-out]">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 w-8 h-8 border-none rounded-full bg-[#2B2420]/[0.06] cursor-pointer text-[#6E635B] text-base flex items-center justify-center"
          >
            ✕
          </button>

          {step === STEPS.RATE && (
            <RateStep onYes={handleYes} onNo={() => setStep(STEPS.FEEDBACK)} />
          )}
          {step === STEPS.FEEDBACK && (
            <FeedbackStep onSubmit={handleFeedbackSubmit} />
          )}
          {step === STEPS.THANKYOU && <ThankYouStep onClose={handleClose} />}
        </div>
      </div>
    </div>
  );
}

export default FeedbackPopup;
